import { Controller } from "@hotwired/stimulus"
import { icon as brandIcon } from "../lib/icons"

export default class extends Controller {
  static targets = ["unitSearchInput", "unitIdField", "residentInfo", "residentIdField", "invoiceLines", "invoiceTotal", "template"]
  static values = {
    unitResidents: Object,
    accounts: Array,
    lineIndex: Number,
    condoId: Number
  }

  connect() {
    this.lineIndexValue = this.invoiceLinesTarget.querySelectorAll('.invoice-line').length || 0
    this.updateResidentInfo()
    this.updateInvoiceTotal()

    // Disable form validation on template fields to prevent submission errors
    this.disableTemplateValidation()

    // Don't automatically add an invoice line - let users do it manually
    // This allows testing of empty form validation
  }



  handleAmountInput(event) {
    // Only update the total, don't modify the input value during typing
    // This prevents cursor jumping while the user is editing
    this.updateInvoiceTotal()
  }

  finalizeAmountFormat(event) {
    // Get the input element from the event
    const input = event.target;

    // Check if input is valid and has a value property
    if (!input || typeof input.value === 'undefined') {
      return
    }

    // Clean the input value by removing non-numeric characters except decimal point
    let cleanValue = input.value.replace(/[^\d.]/g, '')

    // Ensure only one decimal point
    const parts = cleanValue.split('.')
    if (parts.length > 2) {
      cleanValue = parts[0] + '.' + parts.slice(1).join('')
    }

    // Limit decimal places to 2
    if (parts[1] && parts[1].length > 2) {
      cleanValue = parts[0] + '.' + parts[1].substring(0, 2)
    }

    const value = parseFloat(cleanValue)
    if (!isNaN(value) && value >= 0) {
      input.value = value.toFixed(2)
      input.classList.remove('input-error', 'border-error')
    } else if (input.value !== '') {
      input.classList.add('input-error', 'border-error')
    }
    this.updateInvoiceTotal()
  }

  disableTemplateValidation() {
    // Find all form controls in the template and disable their validation
    const templateFields = this.templateTarget.querySelectorAll('input, select, textarea')
    templateFields.forEach(field => {
      // Store original required state for restoration later
      if (field.hasAttribute('required')) {
        field.setAttribute('data-originally-required', 'true')
      }
      // Store original enabled state
      if (!field.disabled) {
        field.setAttribute('data-originally-enabled', 'true')
      }
      // Disable the field completely to prevent it from being validated
      field.setAttribute('disabled', 'disabled')
      field.removeAttribute('required')
      // Also add a special attribute to identify template fields
      field.setAttribute('data-template-field', 'true')
    })
  }

  searchUnits() {
    const query = this.unitSearchInputTarget.value.trim()

    if (query.length < 2) {
      document.getElementById('unit-search-results').innerHTML = ''
      return
    }

    const searchParams = new URLSearchParams({ q: query })
    const condoId = this.condoIdValue || this.element.dataset.condoId

    fetch(`/condos/${condoId}/units?${searchParams}`, {
      headers: {
        'Accept': 'text/vnd.turbo-stream.html'
      }
    })
      .then(response => response.text())
      .then(html => {
        Turbo.renderStreamMessage(html)
      })
      .catch(error => {
        console.error('Error searching units:', error)
      })
  }

  selectUnit(event) {
    const element = event.currentTarget
    const unitId = element.dataset.unitId
    const unitName = element.dataset.unitName
    const residentName = element.dataset.residentName
    const residentEmail = element.dataset.residentEmail
    const residentId = element.dataset.residentId

    // Update form fields
    this.unitSearchInputTarget.value = unitName
    this.unitIdFieldTarget.value = unitId

    // Update resident info display
    this.updateResidentInfoDisplay(residentName, residentEmail, residentId)

    // Clear search results
    document.getElementById('unit-search-results').innerHTML = ''
  }

  updateResidentInfo() {
    const unitId = this.unitIdFieldTarget?.value
    const resident = this.unitResidentsValue[unitId]

    if (unitId && resident) {
      this.updateResidentInfoDisplay(resident.name, resident.email, resident.residentId)
    } else {
      this.residentInfoTarget.innerHTML = '<div class="text-center text-base-content/60">Selecciona una unidad para ver información del residente</div>'
    }
  }

  updateResidentInfoDisplay(name, email, residentId) {
    this.residentInfoTarget.innerHTML = `
      <div class="flex items-center space-x-4">
        <div class="avatar placeholder">
          <div class="bg-neutral-focus text-neutral-content rounded-full w-12">
            <span class="text-xl">${name.charAt(0).toUpperCase()}</span>
          </div>
        </div>
        <div>
          <div class="font-medium">${name}</div>
          <div class="text-sm text-base-content/60">${email || 'Sin email registrado'}</div>
        </div>
      </div>
    `

    // Set hidden resident_id field if it exists
    if (this.hasResidentIdFieldTarget && residentId) {
      this.residentIdFieldTarget.value = residentId
    }
  }

  addInvoiceLine() {
    // Clone the template element instead of using innerHTML to preserve proper structure
    const template = this.templateTarget.firstElementChild.cloneNode(true)

    // Update all name attributes in the cloned template
    const fields = template.querySelectorAll('input, select, textarea')
    fields.forEach(field => {
      if (field.name) {
        field.name = field.name.replace(/NEW_RECORD/g, this.lineIndexValue)
      }
      if (field.id) {
        field.id = field.id.replace(/NEW_RECORD/g, this.lineIndexValue)
      }
    })

    // Update any for attributes in labels
    const labels = template.querySelectorAll('label')
    labels.forEach(label => {
      if (label.getAttribute('for')) {
        label.setAttribute('for', label.getAttribute('for').replace(/NEW_RECORD/g, this.lineIndexValue))
      }
    })

    // Add the cloned and updated element to the container
    this.invoiceLinesTarget.appendChild(template)

    // Re-enable form controls in the newly added line
    const newFields = template.querySelectorAll('input, select, textarea')
    newFields.forEach(field => {
      // Remove template-specific attributes
      field.removeAttribute('disabled')
      field.removeAttribute('data-template-field')

      // Restore required attribute if it was originally required
      if (field.getAttribute('data-originally-required') === 'true') {
        field.setAttribute('required', 'required')
        field.removeAttribute('data-originally-required')
      }

      // Restore enabled state if it was originally enabled
      if (field.getAttribute('data-originally-enabled') === 'true') {
        field.disabled = false
        field.removeAttribute('data-originally-enabled')
      }

      // Clear any default values that might interfere
      if (field.tagName === 'SELECT') {
        field.selectedIndex = 0
      } else if (field.type !== 'hidden') {
        field.value = ''
      }
    })

    this.lineIndexValue++
    this.updateInvoiceTotal()

    // Focus on the first field of the new line for better UX
    const firstField = template.querySelector('select:not([disabled]), input:not([disabled])')
    if (firstField) {
      setTimeout(() => firstField.focus(), 100)
    }
  }

  removeInvoiceLine(event) {
    const button = event.currentTarget
    const lineDiv = button.closest('.invoice-line')
    const destroyField = lineDiv.querySelector('input[name*="[_destroy]"]')

    if (destroyField) {
      destroyField.value = '1'
      lineDiv.style.display = 'none'
    } else {
      lineDiv.remove()
    }

    this.updateInvoiceTotal()
  }

  updateInvoiceTotal() {
    let total = 0

    this.invoiceLinesTarget.querySelectorAll('.invoice-line:not([style*="display: none"]) input[name*="[amount]"]').forEach((input) => {
      const amount = parseFloat(input.value) || 0
      total += amount
    })

    this.invoiceTotalTarget.textContent = this.formatCurrency(total)
  }

  formatCurrency(amount) {
    if (typeof amount === 'object' && amount.currentTarget) {
      // Called as event handler - delegate to finalizeAmountFormat for consistency
      this.finalizeAmountFormat(amount)
      return
    }

    // Called as utility function
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  // Action to handle amount field changes
  amountChanged(event) {
    // Delegate to finalizeAmountFormat for consistency
    this.finalizeAmountFormat(event)
  }

  // Handle form submission to prevent validation errors
  handleSubmit(event) {
    // First, ensure template fields are properly disabled
    const templateFields = this.element.querySelectorAll('[data-template-field="true"]')
    templateFields.forEach(field => {
      field.setAttribute('disabled', 'disabled')
      field.removeAttribute('required')
      field.removeAttribute('name') // Remove name to prevent submission
    })

    // Check if there are any visible invoice lines
    const visibleLines = this.invoiceLinesTarget.querySelectorAll('.invoice-line:not([style*="display: none"])')

    // If no lines exist, allow server-side validation to handle it
    if (visibleLines.length === 0) {
      return // Let the form submit to server for validation
    }

    // Validate the form only if there are lines
    if (!this.validateForm()) {
      event.preventDefault()

      // Show error message
      const errorDiv = document.createElement('div')
      errorDiv.className = 'alert alert-error mb-4'
      errorDiv.innerHTML = `
        <div class="flex">
          ${brandIcon("exclamation-triangle", "w-5 h-5")}
          <div class="ml-2">
            <h4 class="font-semibold">Por favor, completa todos los campos requeridos</h4>
            <p class="text-sm">Asegúrate de que todas las líneas de factura tengan cuenta, descripción y monto válidos.</p>
          </div>
        </div>
      `

      // Insert error message at the top of the form
      this.element.insertBefore(errorDiv, this.element.firstChild)

      // Remove error message after 5 seconds
      setTimeout(() => {
        if (errorDiv.parentNode) {
          errorDiv.remove()
        }
      }, 5000)

      // Scroll to first error field
      const firstErrorField = this.element.querySelector('.select-error, .input-error, .border-error')
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' })
        firstErrorField.focus()
      }
    }
  }

  // Validate form before submission to prevent browser validation errors
  validateForm() {
    // Check if template fields are still present and might cause issues
    const templateFields = this.element.querySelectorAll('[data-template-field="true"]')
    templateFields.forEach(field => {
      // Ensure template fields are completely disabled and won't be validated
      field.setAttribute('disabled', 'disabled')
      field.removeAttribute('required')
    })

    // Validate that all visible invoice lines have required fields filled
    const visibleLines = this.invoiceLinesTarget.querySelectorAll('.invoice-line:not([style*="display: none"])')
    let hasErrors = false

    // Only validate if there are lines to validate
    if (visibleLines.length === 0) {
      return true // Let server handle validation for no lines
    }

    visibleLines.forEach(line => {
      const accountSelect = line.querySelector('select[name*="[account_id]"]')
      const amountInput = line.querySelector('input[name*="[amount]"]')
      const descriptionInput = line.querySelector('input[name*="[description]"]')

      if (accountSelect && !accountSelect.value) {
        accountSelect.classList.add('select-error', 'border-error')
        hasErrors = true
      } else if (accountSelect) {
        accountSelect.classList.remove('select-error', 'border-error')
      }

      if (amountInput && (!amountInput.value || parseFloat(amountInput.value) <= 0)) {
        amountInput.classList.add('input-error', 'border-error')
        hasErrors = true
      } else if (amountInput) {
        amountInput.classList.remove('input-error', 'border-error')
        // Ensure proper formatting
        const value = parseFloat(amountInput.value)
        if (!isNaN(value)) {
          amountInput.value = value.toFixed(2)
        }
      }

      if (descriptionInput && !descriptionInput.value.trim()) {
        descriptionInput.classList.add('input-error', 'border-error')
        hasErrors = true
      } else if (descriptionInput) {
        descriptionInput.classList.remove('input-error', 'border-error')
      }
    })

    return !hasErrors
  }
}
